import axios, { Axios, AxiosRequestConfig } from 'axios';
import * as API from '@bifrost-dev-docs-mfe/common/types/api';
import {
  APIPageData,
  CareersTableData,
  DocExtendSectionData,
  DocSDKToolsSectionData,
  DocumentMetadata,
  FeaturedProductData,
  GreenhouseDepartment,
  TutorialMetadata,
} from '@bifrost-dev-docs-mfe/common/types/api';
import { TutorialData } from '@bifrost-dev-docs-mfe/common/types/tutorials';
import { ApiSpecMetadata } from '@bifrost-dev-docs-mfe/common/types/metadata';
import { Page } from '@bifrost-dev-docs-mfe/common/types/page';

class ApiClient {
  private axios: Axios;

  constructor() {
    this.axios = axios.create();
  }

  getDocumentPage(type: string, lang: string, path: string): Promise<Page> {
    return this.get(`/api/v1/developer/docs/page/${type}/${lang}/${path.replace(/^\//, '')}`);
  }

  getDocumentHtml(type: string, lang: string, path: string): Promise<string> {
    return this.get(`/api/v1/developer/docs/html/${type}/${lang}/${path}`);
  }

  getDocumentMetadata(type: string, lang: string, path: string): Promise<DocumentMetadata> {
    return this.get(`/api/v1/developer/docs/meta/${type}/${lang}/${path.replace(/^\//, '')}`);
  }

  getUseCaseData(lang: string): Promise<API.CardData[]> {
    return this.get(`/api/v1/developer/config/use-cases/${lang}`);
  }

  getUseCaseLanguage(): Promise<API.FilterOption[]> {
    return this.get(`/api/v1/developer/config/use-cases/languages`);
  }

  getUseCaseProducts(): Promise<API.FilterOption[]> {
    return this.get(`/api/v1/developer/config/use-cases/products`);
  }

  getApiDocument(path: string): Promise<API.ApiDoc> {
    return this.get(`/api/v1/developer/api/docs/${path}`, { params: { vendorId: window.branding.vendorId } });
  }

  getApiErrors(type: string): Promise<API.ApiErrorsSpec> {
    return this.get(`/api/v1/developer/api/errors/${type}`, { params: { vendorId: window.branding.vendorId } });
  }

  getDocumentationPageData(lang: string): Promise<API.DocumentationPageData> {
    return this.get(`/api/v1/developer/config/documentation/${lang}`);
  }

  getIntegrationData(lang: string): Promise<API.IntegrationData[]> {
    return this.get(`/api/v1/developer/config/integrations/${lang}`);
  }

  getDataForIntegrationDocument(lang: string, path: string): Promise<API.IntegrationData> {
    return this.get(`/api/v1/developer/config/integration/${lang}/${path}`);
  }

  getChangelogsData(lang: string): Promise<API.ChangelogsData[]> {
    return this.get(`/api/v1/developer/config/changelogs/${lang}`);
  }

  getSidebar(lang: string, path: string, source: string): Promise<API.SidebarData> {
    return this.get(`/api/v1/developer/config/sidebar/${lang}/${path}?source=${source}`);
  }

  getToolsPageData(): Promise<API.ToolsData> {
    return this.get(`/api/v1/developer/config/tools`);
  }

  getRepositoryInfo(repository: string): Promise<API.GithubRepoInfo> {
    return this.get(`/api/v1/developer/github/repo/${repository}`);
  }

  getTutorialData(lang: string, tutorialId: string): Promise<TutorialData> {
    return this.get(`/api/v1/developer/tutorials/content/${lang}/${tutorialId}`);
  }

  getTutorialMetadata(lang: string, tutorialId: string): Promise<TutorialMetadata> {
    return this.get(`/api/v1/developer/tutorials/meta/${lang}/${tutorialId}`);
  }

  getGreenhouseDepartments(): Promise<GreenhouseDepartment[]> {
    return this.get('/api/v1/developer/careers/departments');
  }

  getGreenhouseLocations(): Promise<string[]> {
    return this.get('/api/v1/developer/careers/locations');
  }

  getGreenhouseOpenPositions(): Promise<CareersTableData[]> {
    return this.get('/api/v1/developer/careers/positions');
  }

  getSlackInvitation(email: string): Promise<API.SlackApiResponse> {
    return this.get('/api/v1/developer/slack/invitation', { params: { email } });
  }

  getAllTutorials(lang: string): Promise<API.CardData[]> {
    return this.get(`/api/v1/developer/tutorials/list/${lang}`);
  }

  getTutorialsProducts(): Promise<API.FilterOption[]> {
    return this.get(`/api/v1/developer/tutorials/products`);
  }

  getAPIPageData(): Promise<APIPageData[]> {
    return this.get(`/api/v1/developer/config/api`);
  }

  getAPISpecMetaData(path: string): Promise<ApiSpecMetadata> {
    return this.get(`/api/v1/developer/api/metadata/${path}`, { params: { vendorId: window.branding.vendorId } });
  }

  getDocExtendSectionData(): Promise<DocExtendSectionData[]> {
    return this.get('/api/v1/developer/config/documentation-extend-section');
  }

  getDocSDKToolsSectionData(): Promise<DocSDKToolsSectionData[]> {
    return this.get('/api/v1/developer/config/documentation-sdk-tools-section');
  }

  getFeaturedProducts(lang: string): Promise<FeaturedProductData[]> {
    return this.get(`/api/v1/developer/config/featuredProducts/${lang}`);
  }

  getRegisteredUsersForEvent(eventId: string): Promise<API.UserRegistrationData[]> {
    return this.get(`/api/v1/developer/admin/events/registration/${eventId}`);
  }

  deleteAllEventUsers(eventId: string): Promise<string> {
    return this.delete(`/api/v1/developer/admin/events/registration/${eventId}`);
  }

  getLangConfig(locale: string) {
    return this.get(`/api/v1/developer/config/lang/${locale}`);
  }

  private async get(url: string, config?: AxiosRequestConfig) {
    return (await this.axios.get(url, config)).data;
  }

  private async post(url: string, data: unknown, config?: AxiosRequestConfig) {
    return (await this.axios.post(url, data, config)).data;
  }

  private async delete(url: string, config?: AxiosRequestConfig) {
    return (await this.axios.delete(url, config)).data;
  }
}

export default new ApiClient();
